package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.maps.MapBounds
import fr.frux.core.network.api.TreeApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.core.network.error.toAppError
import fr.frux.shared.data.local.CachedTrees
import fr.frux.shared.data.local.NoOpTreeLocalDataSource
import fr.frux.shared.data.local.TreeLocalDataSource
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.data.mapper.toDto
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.NearbyTrees
import fr.frux.shared.domain.model.ObservableTreeStatus
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreePhoto
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.model.TreeTraitKind
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.dto.CreateTreeRequest
import fr.frux.shared.dto.ErrorResponse
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.withContext

/**
 * Implementation of TreeRepository.
 * Handles API calls and error handling.
 *
 * `getNearbyTrees` est stale-while-revalidate (#60) : cache SQLDelight émis d'abord via
 * [TreeLocalDataSource], réécrit à chaque succès réseau, intact sur erreur. Un succès réseau
 * déclenche aussi [TreeLocalDataSource.prune] pour appliquer la politique de rétention (#62). Le
 * reste des opérations est réseau seul.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class TreeRepositoryImpl(
    private val treeApi: TreeApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
    private val localDataSource: TreeLocalDataSource = NoOpTreeLocalDataSource,
) : TreeRepository {
    private val _statusObserved = MutableSharedFlow<Long>(extraBufferCapacity = 8)
    override val statusObserved: SharedFlow<Long> = _statusObserved.asSharedFlow()

    override fun getNearbyTrees(
        lat: Double,
        lon: Double,
        radius: Double,
        filter: MapFilter,
        limit: Int,
        bounds: MapBounds?,
    ): Flow<Result<NearbyTrees>> =
        flow {
            // Stale-while-revalidate (#60) : le cache local part en premier, le réseau tranche
            // ensuite. Le filtre saison ne s'évalue pas localement (pas de fenêtres de maturité
            // dans le cache) : émettre du cache non filtré montrerait des arbres hors saison.
            // Même exclusion pour le rollup d'espèce : l'agrégation vit dans le référentiel, pas
            // dans le lot caché. Une lecture cache qui casse ne doit jamais coûter le parcours réseau.
            val cached =
                if (filter.inSeason || filter.rollupId != null) {
                    CachedTrees(trees = emptyList(), isStale = false)
                } else {
                    runCatchingCancellable {
                        localDataSource.nearbyTrees(lat, lon, radius, filter.form, filter.part)
                    }.onFailure { Logger.e(TAG, "⚠️ Lecture du cache arbres échouée", it) }
                        .getOrDefault(CachedTrees(trees = emptyList(), isStale = false))
                }
            if (cached.trees.isNotEmpty()) {
                Logger.d(TAG, "💾 ${cached.trees.size} arbres servis depuis le cache local")
                emit(
                    Result.success(
                        NearbyTrees(
                            trees = cached.trees.take(limit),
                            totalAvailable = cached.trees.size,
                            isStale = cached.isStale,
                        ),
                    ),
                )
            }
            val result =
                runCatchingCancellable {
                    // Pas de lat/lon dans les logs : position utilisateur = donnée personnelle (#175, constat Q5).
                    Logger.d(TAG, "🌐 Fetching trees from API: radius=$radius, filter=$filter")
                    val response =
                        treeApi.getNearbyTrees(
                            lat = lat,
                            lon = lon,
                            radius = radius,
                            limit = limit,
                            form = filter.form?.toDto(),
                            part = filter.part?.toDto(),
                            inSeason = filter.inSeason,
                            rollupId = filter.rollupId,
                            bbox = bounds?.toBboxQuery(),
                        )
                    Logger.d(TAG, "📦 Received ${response.trees.size} trees from API")
                    val trees = response.trees.map { it.toDomain() }
                    // Réécriture du cache sur succès seul : une erreur réseau laisse la base
                    // intacte, une écriture qui casse ne prive pas l'UI du lot frais. Le nettoyage
                    // (#62) ne suit qu'une écriture réussie, et son propre échec reste isolé — il
                    // ne doit jamais coûter le lot frais qu'on est en train de rendre.
                    runCatchingCancellable { localDataSource.upsertAll(trees) }
                        .onSuccess {
                            runCatchingCancellable { localDataSource.prune() }
                                .onFailure { Logger.e(TAG, "⚠️ Nettoyage du cache arbres échoué", it) }
                        }.onFailure { Logger.e(TAG, "⚠️ Écriture du cache arbres échouée", it) }
                    NearbyTrees(
                        trees = trees,
                        // Repli sur la taille du lot si le serveur n'a pas renvoye `total` (retro-
                        // compatibilite du champ nullable) : jamais tronque dans ce cas faute de mieux.
                        totalAvailable = response.total ?: response.trees.size,
                    )
                }.onFailure { Logger.e(TAG, "❌ API call failed", it) }
                    .mapFailureToAppError()
            emit(result)
        }.flowOn(dispatcher)

    companion object {
        private const val TAG = "TreeRepository"
        private const val FAVORITES_PAGE_SIZE = 50

        /**
         * Forme `west,south,east,north` du contrat (#621). Le cache local (`nearbyTrees`) reste
         * interrogé au disque seul : il sert alors un sur-ensemble, jamais un trou.
         */
        internal fun MapBounds.toBboxQuery(): String =
            "${southwest.longitude},${southwest.latitude},${northeast.longitude},${northeast.latitude}"
    }

    override suspend fun nearbyNeverSeen(
        lat: Double,
        lon: Double,
        radiusMeters: Double,
    ): Result<Tree?> =
        withContext(dispatcher) {
            runCatchingCancellable {
                val response =
                    treeApi.getNearbyTrees(
                        lat = lat,
                        lon = lon,
                        // Le contrat réseau porte le rayon en kilomètres, l'appelant en mètres.
                        radius = radiusMeters / 1000.0,
                        limit = 1,
                        neverSeen = true,
                    )
                // Le serveur trie déjà par distance croissante (KNN) : le premier du lot est le
                // plus proche, pas besoin de retrier côté client.
                // Rien n'est écrit dans le cache local ici : ce lot n'est pas un résultat de carte
                // filtré par l'utilisateur, et le mettre en cache désignerait ces arbres comme
                // « vus » par la prochaine lecture stale-while-revalidate.
                response.trees.firstOrNull()?.toDomain()
            }.onFailure { Logger.e(TAG, "❌ Recherche « jamais vu » échouée", it) }
                .mapFailureToAppError()
        }

    override suspend fun nearbyTreesSnapshot(
        lat: Double,
        lon: Double,
        radiusMeters: Double,
        limit: Int,
    ): Result<List<Tree>> =
        withContext(dispatcher) {
            runCatchingCancellable {
                val response =
                    treeApi.getNearbyTrees(
                        lat = lat,
                        lon = lon,
                        // Le contrat réseau porte le rayon en kilomètres, l'appelant en mètres.
                        radius = radiusMeters / 1000.0,
                        limit = limit,
                    )
                response.trees.map { it.toDomain() }
            }.onFailure { Logger.e(TAG, "❌ Recherche de doublons échouée", it) }
                .mapFailureToAppError()
        }

    override fun getTreeById(id: Long): Flow<Result<Tree>> =
        flow {
            emit(runCatchingCancellable { treeApi.getTreeById(id).toDomain() }.mapFailureToAppError())
        }.flowOn(dispatcher)

    override suspend fun observeTreeStatus(
        id: Long,
        status: ObservableTreeStatus,
    ): Result<Unit> =
        runCatchingCancellable { treeApi.observeTreeStatus(id, status.toDto()) }
            .onSuccess { _statusObserved.tryEmit(id) }
            .onFailure { Logger.e(TAG, "❌ Observation de statut échouée (tree $id)", it) }
            .mapFailureToAppError()
            .map { }

    override suspend fun addTreeTrait(
        id: Long,
        kind: TreeTraitKind,
    ): Result<Unit> =
        runCatchingCancellable { treeApi.addTreeTrait(id, kind.toDto()) }
            .onFailure { Logger.e(TAG, "❌ Ajout de trait échoué (tree $id)", it) }
            .mapFailureToAppError()

    override suspend fun removeTreeTrait(
        id: Long,
        kind: TreeTraitKind,
    ): Result<Unit> =
        runCatchingCancellable { treeApi.removeTreeTrait(id, kind.toDto()) }
            .onFailure { Logger.e(TAG, "❌ Retrait de trait échoué (tree $id)", it) }
            .mapFailureToAppError()

    override suspend fun createTree(
        speciesId: Long,
        latitude: Double,
        longitude: Double,
        status: TreeStatus?,
        certainty: Certainty,
        description: String?,
        idempotencyKey: String,
    ): Result<Tree> =
        runCatchingCancellable {
            treeApi
                .createTree(
                    CreateTreeRequest(
                        speciesId = speciesId,
                        latitude = latitude,
                        longitude = longitude,
                        // UNKNOWN explicite : une API publiée avant le lot 1 refuserait l'absence d'état.
                        status = (status ?: TreeStatus.UNKNOWN).toDto(),
                        description = description,
                        certainty = certainty.toDto(),
                    ),
                    idempotencyKey,
                ).toDomain()
        }.onFailure { Logger.e(TAG, "Échec de la création d'arbre", it) }
            .mapFailureToAppError()

    override suspend fun uploadPhoto(
        treeId: Long,
        imageBytes: ByteArray,
        contentType: String,
        onProgress: (Float) -> Unit,
    ): Result<TreePhoto> =
        runCatchingCancellable {
            treeApi
                .uploadPhoto(
                    treeId = treeId,
                    bytes = imageBytes,
                    contentType = contentType,
                    onProgress = { sent, total -> uploadFraction(sent, total)?.let(onProgress) },
                ).toDomain()
        }.onFailure { Logger.e(TAG, "Échec de l'upload de photo", it) }
            .mapFailureToAppError()

    override fun getFavoriteTrees(): Flow<Result<List<Tree>>> =
        flow {
            emit(
                runCatchingCancellable {
                    // Le store des favoris veut l'ensemble, pas la première page : on concatène
                    // jusqu'à une page incomplète — le serveur ne rend pas de curseur exploitable ici.
                    buildList {
                        var page = 1
                        while (true) {
                            val batch = treeApi.getFavoriteTrees(page = page, limit = FAVORITES_PAGE_SIZE)
                            addAll(batch)
                            if (batch.size < FAVORITES_PAGE_SIZE) break
                            page++
                        }
                    }.map { it.toDomain() }
                }.onFailure { Logger.e(TAG, "❌ Chargement des favoris échoué", it) }
                    .mapFailureToAppError(),
            )
        }.flowOn(dispatcher)

    override fun getMyTrees(): Flow<Result<List<Tree>>> =
        flow {
            emit(
                runCatchingCancellable { treeApi.getMyTrees().map { it.toDomain() } }
                    .onFailure { Logger.e(TAG, "❌ Chargement de mes arbres échoué", it) }
                    .mapFailureToAppError(),
            )
        }.flowOn(dispatcher)

    override suspend fun setFavorite(
        id: Long,
        favorite: Boolean,
    ): Result<Unit> =
        runCatchingCancellable {
            if (favorite) treeApi.favoriteTree(id) else treeApi.unfavoriteTree(id)
        }.mapFailureToAppError()

    override suspend fun reportTree(
        id: Long,
        suggestedSpeciesId: Long?,
        kind: String,
    ): Result<Tree> =
        runCatchingCancellable { treeApi.reportTree(id, suggestedSpeciesId, kind).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Signalement échoué (tree $id)", it) }
            .mapFailureToAppError()

    override suspend fun withdrawTreeReport(
        id: Long,
        kind: String,
    ): Result<Tree> =
        runCatchingCancellable { treeApi.withdrawTreeReport(id, kind).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Retrait du signalement échoué (tree $id)", it) }
            .mapFailureToAppError()

    override suspend fun resolveTreeReports(id: Long): Result<Tree> =
        runCatchingCancellable { treeApi.resolveTreeReports(id).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Résolution des signalements échouée (tree $id)", it) }
            .mapFailureToAppError()

    override suspend fun reportPhoto(photoId: String): Result<Unit> =
        runCatchingCancellable { treeApi.reportPhoto(photoId) }
            .onFailure { Logger.e(TAG, "❌ Signalement de photo échoué (photo $photoId)", it) }
            .mapFailureToAppError()

    override suspend fun deletePhoto(photoId: String): Result<Unit> =
        runCatchingCancellable { treeApi.deletePhoto(photoId) }
            .onFailure { Logger.e(TAG, "❌ Suppression de photo échouée (photo $photoId)", it) }
            .mapFailureToAppError()

    override suspend fun deleteTree(id: Long): Result<Unit> =
        runCatchingCancellable { treeApi.deleteTree(id) }
            .onFailure { Logger.e(TAG, "❌ Suppression de l'arbre échouée (tree $id)", it) }
            .mapFailureToAppError()
}

/** Fraction d'envoi bornée à 0..1, `null` tant que le total est inconnu ou nul. */
internal fun uploadFraction(
    sent: Long,
    total: Long?,
): Float? = if (total == null || total <= 0L) null else (sent.toFloat() / total).coerceIn(0f, 1f)
